var util = require('lib/util');

var EventEmitter = require('events').EventEmitter;

var createElement = require('lib/create-element').createElement,
    html = require('lib/create-element').createElementsFromHTML,
    css = require('lib/css-object'),
    millicolor = require('lib/color-object');

function toUISkinKey(name) {
    return name.toLowerCase().replace(/\W/g, '');
}

function toArray(arg, from) {
    if (util.isNumber(from)) {
        return Array.prototype.slice.call(arg, from);
    }
    return Array.prototype.slice.call(arg);
}

function createTag(tag, options, content) {
    var attr = options;

    if (util.isString(options)) {
        tag += options;
        attr = {};
    }
    return createElement(tag, attr, toArray(arguments, 2));
}

function isUISkinComponent(obj) {
    return util.isObject(obj) && obj.hasOwnProperty('$el');
}

var UISkinElement = util.defineClass(EventEmitter,
    /**
     * @classdesc The class that provides the base interface for all UI objects.
     * @hideconstructor
     * @constructs
     * @implements {external:EventEmitter}
     */
    function () {
        this.setMaxListeners(0); // Infinity
    },
    /** 
     * @lends UISkinElement.prototype 
     */
    {
        /**
         * The root node of the object.
         * 
         * @abstract
         * @member {HTMLElement} $el 
         * @memberof UISkinElement
         */
        emit: function (name) {
            if (!PRODUCTION) {
                console.log('EVENT: ', Array.prototype.slice.call(arguments), this);
            }
            EventEmitter.prototype.emit.apply(this, arguments);
        },
        /**
         * Inserts a set of {@link UISkinComponent} objects, `Node` objects or `DOMString` objects 
         * after the last child of the root node of the object.
         * 
         * @param {...(UISkinComponent|HTMLElement|string)} child
         */
        append: function () {
            if (this.$el) {
                Element.prototype.append.apply(this.$el,
                    Array.prototype.slice.call(arguments).map(function (arg) {
                        if (isUISkinComponent(arg)) {
                            return arg.$el || '';
                        }
                        return arg || '';
                    })
                );
            }
        },
        /**
         * Removes a set of {@link UISkinComponent} objects or `Node` objects from the DOM.
         * The string argument is treated as a CSS selector that defines the nodes to be removed from the DOM. 
         * 
         * If the argument is omitted, the root node of the object itself is removed from the DOM.
         * 
         * @param {...(UISkinComponent|HTMLElement|CSSSelector)} [child]
         */
        remove: function () {
            if (!arguments.length) {
                if (isUISkinComponent(this)) {
                    if (this.$el) {
                        this.$el.parentNode.removeChild(this.$el);
                    }
                }
            } else {
                Array.prototype.slice.call(arguments).forEach(function (el) {
                    if (typeof el === 'string' && this.$el) {
                        el = this.$el.querySelectorAll(el);
                        this.remove.apply(this, Array.prototype.slice.call(el));
                        return;
                    } else if (isUISkinComponent(el)) {
                        el = el.$el;
                    }
                    if (el && el.parentNode) {
                        el.parentNode.removeChild(el);
                    }
                }, this);
            }
        }
    }
);

var UISkinComponent = util.defineClass(UISkinElement,
    /**
     * @classdesc The class that provides an interface for the UI component object.
     * @hideconstructor
     * @constructs
     * @implements {UISkinElement}
     */
    function () {
        UISkinElement.call(this);
    },
    /** 
     * @lends UISkinComponent.prototype 
     */
    {
        /**
         * Assigns a DOM node to the component instance.
         * 
         * @memberof UISkinComponent.prototype 
         * @method
         * @param {HTMLElement} element
         * @returns {UISkinComponent} This object itself
         * @example 
define(function (require, exports, module) {
    module.exports = function (skin, options, solo) {
        var element = skin.createElement('span', options.text);
        // "this" is UISkinComponent instance 
        return this.exports(element); 
    }
});
         */
        exports: function (element, props) {
            Object.defineProperties(this, {
                /**
                 * The root node of the component.
                 * 
                 * @member {HTMLElement} $el
                 * @readonly
                 * @memberof UISkinComponent
                 */
                '$el': {
                    value: element
                },
                'element': {
                    value: element
                }
            });
            if (element) {
                if (element.classList) {
                    /**
                     * Reference to the `classList` property of the root node.
                     * 
                     * @member {DOMTokenList} classList
                     * @readonly
                     * @memberof UISkinComponent
                     */
                    Object.defineProperty(this, 'classList', {
                        value: element.classList
                    });
                }
            }
            if (props) {
                for (var key in props) {
                    if (props.hasOwnProperty(key)) {
                        switch (typeof props[key]) {
                            case 'function':
                                Object.defineProperty(this, key, {
                                    configurable: true,
                                    enumerable: true,
                                    writable: true,
                                    value: props[key]
                                });
                                break;
                            default:
                                Object.defineProperty(this, key, props[key]);

                        }
                    }
                }
            }
            return this;
        }
    }
);

var UISkinController = util.defineClass(UISkinElement,
    /**
     * @classdesc The class that provides an interface for the UI controller object.
     * 
     * The UI controller object is linked to a box area in the HTML layout where UI elements will be added, which is called the skin area.
     * 
     * @hideconstructor
     * @constructs
     * @implements {UISkinElement}
     */
    function (name, baseElement) {
        UISkinElement.call(this);

        this.name = name;

        var rootNode = document.body;
        if (baseElement) {
            rootNode = baseElement;
        } else if (Cortona3DSolo.core && Cortona3DSolo.core.canvas) {
            rootNode = Cortona3DSolo.core.canvas.parentNode;
        } else if (Cortona3DSolo.drawing && Cortona3DSolo.drawing.svg) {
            rootNode = Cortona3DSolo.drawing.svg.parentNode;
        }

        var element = rootNode.querySelector('.skin-holder');

        if (!element || element.parentNode !== rootNode) {
            element = (function (div) {
                rootNode.insertBefore(div, rootNode.firstChild).className = 'skin-holder';
                return div;
            })(document.createElement('div'));
        }

        element.classList.add('skin-holder-' + toUISkinKey(this.name));

        Object.defineProperties(this, {
            /**
             * The root node of the skin area.
             * 
             * @member {HTMLElement} $el
             * @readonly
             * @memberof UISkinController
             */
            '$el': {
                value: element
            },
            'element': {
                value: element
            },
            /**
             * Reference to the `classList` property of the root node.
             * 
             * @member {DOMTokenList} classList
             * @readonly
             * @memberof UISkinController
             */
            'classList': {
                value: element.classList
            }
        });
    },
    /** 
     * @lends UISkinController.prototype 
     */
    {
        /**
         * Creates an instance of the component as needed and appends it to the skin area root node if the component is an object of type {@link UISkinComponent} or HTMLElement.
         * 
         * The argument can be:
         * - a factory function of the component - {@link UISkinComponentFactory}
         * - the instance of the component - {@link UISkinComponent}
         * - an HTML node
         * - a string with the module name of the component
         * - an object with two properties `component` and `option`
         * - a Promise object that returns any object of the types listed here
         * - an array containing any object of the types listed here
         * 
         * If an array is used as an argument, the return value is an array of created components.
         * 
         * If an asynchronous load is required when creating a component instance, 
         * i.e. a string with the name of the component module or a Promise object is used, 
         * the Promise object is returned with the result.
         * 
         * @param {(UISkinComponent|UISkinComponentFactory|HTMLElement|string|object|Promise|Array)} components 
         * @param {object} [options] An options object that apply to factory 
         * @returns {UISkinComponent|Promise.<UISkinComponent>|HTMLElement|Array}
         * @example <caption>HTML element</caption>
var skin = solo.skin.create('app');
skin.render(skin.create('span', 'A message')); // -> HTMLElement
         * @example <caption>Component module</caption>
skin.render('components/wait', { type: 'win8' }); // -> Promise.<UISkinComponent>
         * @example <caption>Component factory function</caption>
skin.render(function (skin, options, solo) {
    return this.exports(skin.create('span', options.message || ''));
}); // -> UISkinComponent
         * @example <caption>A Promise object that returns the component factory function</caption>
skin.render(solo.skin.require('components/wait'), { type: 'win8' }); // -> Promise.<UISkinComponent>
         * @example <caption>A Promise object that returns the component instance</caption>
skin.render(skin.use('components/wait', { type: 'win8' })); // -> Promise.<UISkinComponent>
         * @example <caption>An object</caption>
skin.render({
    component: require('components/wait'),
    options: {
        type: 'win8'
    }
}, options); // -> UISkinComponent
         * @example <caption>An array</caption>
skin.render([ ... ], options); // -> UISkinComponent[]
         * @example <caption>An array with the asynchronous loading of elements</caption>
skin.render([
    skin.create('h3', 'Component sample'), 
    'components/wait'
], { type: 'win8' }); // -> Promise.<[HTMLElement, UISkinComponent]>
         */
        render: function (components, options) {
            var self = this,
                component;
            if (components) {
                if (util.isString(components)) {
                    return this.render(this.use(components, options), options);
                } else if (util.isFunction(components)) {
                    component = this.create(components, options);
                    return this.render(component, options);
                } else if (util.isObject(components)) {
                    if (isUISkinComponent(components) || util.isHTMLNode(components)) {
                        this.append(components);
                        return components;
                    } else if (util.isPromise(components)) {
                        return components.then(function (component) {
                            return self.render(component, options);
                        });
                    } else if (components.forEach) {
                        res = components.map(function (item) {
                            return this.render(item, options);
                        }, this);
                        return res.some(function (component) {
                            return util.isPromise(component);
                        }) ? Promise.all(res) : res;
                    } else if (util.isObject(components.rules) && util.isFunction(components.render)) {
                        components.render();
                        return components;
                    } else if (components.component) {
                        return this.render(components.component, Cortona3DSolo.expand(components.options || {}, options || {}));
                    }
                }
            }
            return components;
        },
        /**
         * @private
         */
        register: function (factory, options) {
            return this.create(factory, options);
        },
        /**
         * Creates an instance of the component.
         * 
         * Loads the module with the factory function, if necessary, 
         * performs the factory function of the component and returns the result.
         * 
         * @async
         * @param {string|UISkinComponentFactory} name 
         * - a string with the module name of the component
         * - factory function of the component - {@link UISkinComponentFactory}
         * @param {*} options 
         * @returns {Promise.<UISkinComponent>} `Promise` whose fulfillment handler receives an {@link UISkinComponent} instance of the requested component.
         */
        use: function (name, options) {
            var self = this;

            if (util.isString(name)) {
                return Cortona3DSolo.skin.require(name)
                    .then(function (factory) {
                        return self.create(factory, options);
                    });
            }

            if (util.isFunction(name)) {
                // use(factory, options)
                return Promise.resolve(this.create(name, options));
            }

            return Promise.reject(new Error('The first argument in use() must be string or factory function.'));
        },
        /**
         * Shows the HTML element.
         * If the argument is omitted, shows the contents of the skin area itself.
         * 
         * @param {HTMLElement|CSSSelector} [el] HTML node or CSS selector
         */
        show: function (el) {
            if (util.isString(el)) {
                el = document.querySelector(el);
            } else if (util.isUndefined(el)) {
                el = this.$el;
            }
            if (el) {
                el.style.visibility = 'visible';
                el.style.display = '';
            }
            return el;
        },
        /**
         * Hides the HTML element.
         * If the argument is omitted, hides the contents of the skin area itself.
         * 
         * @param {HTMLElement|CSSSelector} [el] HTML node or CSS selector
         * @param {boolean} [force=false] 
         * - `false` - `style.visibility='hidden'` is used
         * - `true` - `style.dispaly='none'` is used
         */
        hide: function (el, force) {
            if (util.isString(el)) {
                el = document.querySelector(el);
            } else if (util.isUndefined(el)) {
                el = this.$el;
                force = true;
            }
            if (el) {
                el.style.visibility = 'hidden';
                if (force) {
                    el.style.display = 'none';
                }
            }
            return el;
        },
        /**
         * Toggles visibility of the HTML element.
         * If the argument is omitted, toggles visibility of the skin area itself.
         * 
         * @param {HTMLElement|CSSSelector} [el] HTML node or CSS selector
         * @param {boolean} [visibility] 
         * @returns {boolean}
         */
        toggle: function (el, visibility) {
            if (util.isString(el)) {
                el = document.querySelector(el);
            } else if (typeof el === "boolean") {
                el = this.$el;
                visibility = el;
            } else if (util.isUndefined(el)) {
                el = this.$el;
            }
            if (el) {
                if (util.isUndefined(visibility)) visibility = el.style.display === 'none';
                el.style.display = visibility ? '' : 'none';
                el.style.visibility = visibility ? 'visible' : 'hidden';
            }
            return visibility;
        },
        /**
         * Clears contents of the HTML element.
         * If the argument is omitted, the contents of the skin root node are cleared.
         * 
         * @param {HTMLElement|CSSSelector} [el] HTML node or CSS selector
         */
        clear: function (el) {
            if (util.isString(el)) {
                el = document.querySelector(el);
            } else if (util.isUndefined(el)) {
                el = this.$el;
            }
            if (el) {
                el.innerHTML = '';
            }
        },
        /**
         * Creates an HTML element or instance of a component.
         * 
         * @param {string|UISkinComponentFactory} factory
         * @param {object} [options] Contains properties needed to create a new HTML element or UISkinComponent object.
         * @param {...*} [content] The contents of the new HTML element.
         * @returns {HTMLElement|UISkinComponent}
         * @see {@link module:solo.app.ui.createElement app.ui.createElement()}
         * @example
skin.create('span#msg', 'A message'); // -> HTMLElement
skin.create(require('components/wait'), { type: 'win8' }); // -> UISkinComponent
         */
        create: function (factory, options) {
            function resolveUISkinComponent(child) {
                if (Array.isArray(child)) {
                    return child.map(resolveUISkinComponent);
                } else if (isUISkinComponent(child)) {
                    return child.$el || '';
                } else {
                    return child;
                }
            }
            if (util.isFunction(factory)) {
                var component = new UISkinComponent(),
                    res = factory.call(component, this, options || {}, Cortona3DSolo);
                if (!util.isUndefined(res)) {
                    component = res;
                }
                return component || {};
            } else {
                return createElement.apply(this, Array.prototype.slice.call(arguments).map(resolveUISkinComponent));
            }
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        toolbar: function (options, content) {
            var el = this.div.apply(this, arguments);
            el.classList.add('skin-toolbar');
            return el;
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        container: function (options, content) {
            var el = this.div.apply(this, arguments);
            el.classList.add('skin-container');
            return el;
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        panel: function (options, content) {
            var el = this.div.apply(this, arguments);
            el.classList.add('skin-panel');
            return el;
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        button: function (options, content) {
            var el = createTag.apply(this, ['button'].concat(toArray(arguments)));
            el.classList.add('skin-control');
            return el;
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        buttonImg: function (options, content) {
            var el = this.span.apply(this, arguments);
            el.classList.add('skin-img-button');
            return el;
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        input: function (options, content) {
            var el = createTag.apply(this, ['input'].concat(toArray(arguments)));
            el.classList.add('skin-control');
            return el;
        },
        /**
         * Creates an HTML element `<select>`
         * @param {object} options The properties of the new element.
         * @param {object[]} items An array of objects with the contents of `<select>` items.
         * The object must contains properties:
         * - `description` - the `<option>` contents
         * - `value` - value of the attribute of the same name
         * - `selected` - value of the attribute of the same name
         * A string can be used as the content of an element `<option>` instead of an object.
         */
        select: function (options, items) {
            var el = createTag.apply(this, ['select', options].concat(
                items.map(function (option) {
                    var o = option;
                    if (util.isString(option)) {
                        o = {
                            description: option
                        };
                    }
                    return this.create('option', {
                        selected: o.selected,
                        value: o.value
                    }, o.description);
                }, this)
            ));
            el.classList.add('skin-control');
            return el;
        },
        /**
         * @private
         * @param {string} type "ul" or "ol"
         * @param {object} options
         * @param {object[]} items
         */
        list: function (type, options, items) {
            if (type !== 'ul' && type !== 'ol') {
                console.error('Type of list must be "ol" or "ul" only.');
            }
            var el = createTag.apply(this, [type, options].concat(
                items.map(function (item) {
                    return this.create('li', {}, item);
                }, this)
            ));
            return el;
        },
        /**
         * Creates the HTML `<ul>` element
         * 
         * @param {object} options The properties of the new element.
         * @param {object[]} items An array of objects to create the content of `<li>` elements. This can be a string or HTML element.
         */
        ul: function (options, items) {
            return this.list('ul', options, items);
        },
        /**
         * Creates the HTML `<ol>` element
         * 
         * @param {object} options The properties of the new element.
         * @param {object[]} items An array of objects to create the content of `<li>` elements. This can be a string or HTML element.
         */
        ol: function (options, items) {
            return this.list('ol', options, items);
        },
        /**
         * Creates the HTML `<dl>` element
         * 
         * @param {object} options The properties of the new element.
         * @param {object[]} items An array of objects with the contents of `<dl>` items.
         * The object must contains properties:
         * - `term` - the `<dt>` contents
         * - `def` - the `<dd>` contents
         */
        dl: function (options, items) {
            var self = this;
            var el = createTag.apply(this, ['dl', options].concat(
                items.reduce(function (a, item) {
                    return a.concat(
                        self.create('dt', {}, item.term),
                        self.create('dd', {}, item.def)
                    );
                }, [])
            ));
            return el;
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        text: function (options, content) {
            var el = this.span.apply(this, arguments);
            el.classList.add('skin-text');
            return el;
        },
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        label: createTag.bind(this, 'label'),
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        p: createTag.bind(this, 'p'),
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        a: createTag.bind(this, 'a'),
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        span: createTag.bind(this, 'span'),
        /**
         * @private
         * @param {object} options
         * @param {...*} [content]
         */
        div: createTag.bind(this, 'div'),
        /**
         * Checks if the HTML element is hidden.
         * 
         * @param {HTMLElement} node
         * @returns {boolean}
         */
        isHidden: function (node) {
            return !node.offsetWidth && !node.offsetHeight;
        },
        /**
         * Checks if the HTML element is visible in the scroll area on the screen.
         * 
         * @param {HTMLElement} node
         * @param {boolean} [checkHorizontal=false] Checks also the offset on the horizontal axis.
         * @returns {boolean}
         */
        isVisible: function (node, checkHorizontal) {
            var nodeRect = node.getBoundingClientRect(),
                thisRect = (this.$el || this.element).getBoundingClientRect();
            return (nodeRect.top >= thisRect.top && nodeRect.bottom <= thisRect.bottom) &&
                (!checkHorizontal || (nodeRect.left >= thisRect.left && nodeRect.right <= thisRect.right));
        },
        createElement: createElement,
        html: html,
        css: css,
        color: millicolor
    }
);

module.exports = UISkinController;

/**
 * A function that creates a new instance of a user interface component.
 * 
 * @param {UISkinController} skin
 * @param {object} options
 * @param {Cortona3DSolo} solo
 * @typedef {function} UISkinComponentFactory
 * @this UISkinComponent
 * @example
function (skin, options, solo) {
    return this.exports(skin.create('#message', options.message | ''));
}
 */

/**
 * A CSS selector that directly match one element in a document, based on the type of element, class, or id.
 * 
 * @typedef {string} CSSSelector
 * @example 
span#ident.class-one.class-two
 */